import { useOutletContext } from 'react-router-dom'

import {
  Box,
  CollapsableCard,
  RadioButton,
  Section,
  Text,
} from '@factorialco/deprecated-design-system'
import {
  ProjectDetailsPageSettingsPageBudgetSectionFragment,
  ProjectManagementValueObjectsBudgetStrategyTypeEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { Result, UnhandledResult, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import ProjectBudgetForm from 'modules/project_management/components/forms/ProjectBudgetForm'
import { IProjectBudgetValues } from 'modules/project_management/components/forms/ProjectBudgetStrategiesForm'
import { ProjectBudgetStrategiesForm } from 'modules/project_management/components/forms/ProjectBudgetStrategiesForm'
import {
  useCreateBudgetStrategy,
  useDeleteBudgetStrategy,
  useUpdateBudgetStrategy,
} from 'modules/project_management/hooks/planningMutations/budgetStrategyHooks'
import { budgetTypesEnum } from 'modules/project_management/lib/constants'
import {
  getDecimalHoursFromMinutes,
  getMinutesFromDecimalHours,
} from 'modules/project_management/lib/formatHours'
import { isValidBudgetType } from 'modules/project_management/lib/utils'

export const FixedBudgetSection: React.FC<{
  project: ProjectDetailsPageSettingsPageBudgetSectionFragment
  hasSubprojectsFeature: boolean
}> = ({ project, hasSubprojectsFeature }) => {
  const featureAvailable = hasSubprojectsFeature

  const { company } = useCurrentSession()

  const { createBudgetStrategy } = useCreateBudgetStrategy(company.id)
  const { updateBudgetStrategy } = useUpdateBudgetStrategy()
  const { deleteBudgetStrategy } = useDeleteBudgetStrategy()

  // Helper to get current budget type
  const getCurrentBudgetType = (): budgetTypesEnum => {
    if (project.budgetStrategy && isValidBudgetType(project.budgetStrategy.budgetType)) {
      return project.budgetStrategy.budgetType
    }
    return budgetTypesEnum.withoutBudget
  }

  // Different options for mutation
  const getMutation = (data: IProjectBudgetValues): Promise<UnhandledResult<Result<unknown>>> => {
    const currentBudgetType = getCurrentBudgetType()
    const targetBudgetType = data.budgetType

    const currentDelegation = project.budgetStrategy?.delegated ?? false
    const targetDelegation = data.delegated
    const plannedCents =
      Number(data.plannedCents) === 0 ? undefined : Math.round(Number(data.plannedCents) * 100)
    const plannedMinutes =
      Number(data.plannedMinutes) === 0
        ? undefined
        : getMinutesFromDecimalHours(Number(data.plannedMinutes))
    const feeAmountCents = data.feeAmountCents
      ? Math.round(Number(data.feeAmountCents) * 100)
      : undefined
    const fixedCostCents = data.fixedCostCents
      ? Math.round(Number(data.fixedCostCents) * 100)
      : undefined

    // Handle deletion (withoutBudget)
    if (targetBudgetType === budgetTypesEnum.withoutBudget && data.delegated === false) {
      if (!project.budgetStrategy) {
        throw new Error('No budget to delete')
      }
      return deleteBudgetStrategy({ id: project.budgetStrategy.id })
      // it should also update the project if the fixed cost cents are valid (more than 0)
    }

    // Handle updates (same type)
    if (currentBudgetType === targetBudgetType && currentDelegation === targetDelegation) {
      const budgetStrategy = project.budgetStrategy
      if (!budgetStrategy) {
        throw new Error('No budget strategy to update')
      }
      if (
        targetBudgetType !== budgetTypesEnum.projectFixedCost &&
        targetBudgetType !== budgetTypesEnum.totalBudget
      ) {
        throw new Error('Cannot update this budget type')
      }
      // Map to GraphQL enum type safely after validation
      const validBudgetStrategyType: ProjectManagementValueObjectsBudgetStrategyTypeEnum =
        targetBudgetType === budgetTypesEnum.projectFixedCost
          ? 'project_fixed_cost'
          : 'total_budget'

      return updateBudgetStrategy(
        {
          id: budgetStrategy.id,
          budgetAllocation: undefined,
          budgetStrategyType: validBudgetStrategyType,
          plannedCents,
          plannedMinutes,
          feeAmountCents,
          fixedCostCents,
          isBillable: data.isBillable ?? false,
        },
        true
      )
    }

    // Handle creation/switching between types
    // Map to GraphQL enum type safely
    const getBudgetStrategyType = (): ProjectManagementValueObjectsBudgetStrategyTypeEnum => {
      if (targetBudgetType === budgetTypesEnum.projectFixedCost) return 'project_fixed_cost'
      if (targetBudgetType === budgetTypesEnum.totalBudget) return 'total_budget'
      if (targetBudgetType === budgetTypesEnum.withoutBudget) return 'without_budget'
      // For delegated budgets with other types, throw error as they're not valid GraphQL enum values
      throw new Error(`Unknown budget type: ${targetBudgetType}`)
    }

    if (
      targetBudgetType !== budgetTypesEnum.projectFixedCost &&
      targetBudgetType !== budgetTypesEnum.totalBudget &&
      data.delegated === false
    ) {
      throw new Error(`Unknown budget type: ${targetBudgetType}`)
    }

    return createBudgetStrategy(
      {
        projectId: project.id,
        budgetStrategyType: getBudgetStrategyType(),
        budgetAllocation: undefined,
        plannedCents,
        plannedMinutes,
        feeAmountCents,
        fixedCostCents,
        isBillable: data.isBillable ?? false,
        delegated: targetDelegation,
      },
      true
    )
  }

  const { hasCostsFeature } = useOutletContext<{
    hasCostsFeature: boolean
  }>()

  const getProjectBudgetDefaultValues = () => {
    const currentBudgetType = getCurrentBudgetType()
    const baseCurrency = { currencyType: project.legalEntity.currency }

    const baseValues = {
      fixedCostCents: project.fixedCostCents ? project.fixedCostCents / 100 : undefined,
      isBillable: project.isBillable ?? false,
      ...baseCurrency,
    }

    const budgetStrategy = project.budgetStrategy

    if (!budgetStrategy || currentBudgetType === budgetTypesEnum.withoutBudget) {
      return {
        budgetType: budgetTypesEnum.withoutBudget,
        budgetAllocation: undefined,
        feeAmountCents: undefined,
        plannedCents: undefined,
        plannedMinutes: undefined,
        delegated: project.budgetStrategy?.delegated ?? false,
        ...baseValues,
      }
    }

    return {
      budgetType: currentBudgetType,
      budgetAllocation: null,
      budgetAllocationCents: null,
      feeAmountCents: budgetStrategy.feeAmountCents
        ? budgetStrategy.feeAmountCents / 100
        : undefined,
      plannedCents: budgetStrategy.plannedCents ? budgetStrategy.plannedCents / 100 : undefined,
      plannedMinutes: budgetStrategy.plannedMinutes
        ? getDecimalHoursFromMinutes(budgetStrategy.plannedMinutes)
        : undefined,
      delegated: budgetStrategy.delegated ?? false,
      ...baseValues,
    }
  }

  const { values, errors, setValues, setErrors, handleSubmit, dirty, resetForm } =
    useForm<IProjectBudgetValues>({
      defaultValues: getProjectBudgetDefaultValues(),
      enableReinitialize: true,
      validateOnChange: true,
      validateOnBlur: true,
      validate: (data) => {
        const errorValidation: FormErrors<IProjectBudgetValues> = {}
        if (data.budgetType === budgetTypesEnum.projectFixedCost && data.plannedCents) {
          if (data.plannedCents < 0) {
            errorValidation.plannedCents = i18n.t(
              'project_management.forms.financial_details.fixed_costs.negative_error'
            )
          }
          if (isNaN(data.plannedCents)) {
            errorValidation.plannedCents = i18n.t(
              'project_management.planning.validation_errors.field_is_number'
            )
          }
        }
        if (data.budgetType === budgetTypesEnum.projectFixedCost && data.feeAmountCents) {
          if (data.feeAmountCents < 0) {
            errorValidation.feeAmountCents = i18n.t(
              'project_management.forms.financial_details.fixed_costs.negative_error'
            )
          }
          if (isNaN(data.feeAmountCents)) {
            errorValidation.feeAmountCents = i18n.t(
              'project_management.planning.validation_errors.field_is_number'
            )
          }
        }

        if (data.budgetType === budgetTypesEnum.totalBudget) {
          if (data.plannedCents !== undefined && data.plannedCents !== null) {
            if (isNaN(data.plannedCents)) {
              errorValidation.plannedCents = i18n.t(
                'project_management.forms.financial_details.fixed_costs.format_error'
              )
            } else if (data.plannedCents < 0) {
              errorValidation.plannedCents = i18n.t(
                'project_management.forms.financial_details.fixed_costs.negative_error'
              )
            }
          }
          if (data.plannedMinutes !== undefined && data.plannedMinutes !== null) {
            if (isNaN(data.plannedMinutes)) {
              errorValidation.plannedMinutes = i18n.t(
                'project_management.forms.financial_details.fixed_costs.format_error'
              )
            } else if (data.plannedMinutes < 0) {
              errorValidation.plannedMinutes = i18n.t(
                'project_management.forms.financial_details.fixed_costs.negative_error'
              )
            }
          }
        }

        return errorValidation
      },
      onSubmit: async (data) => {
        const result = await getMutation(data)
        const handled = handleResult(result, (err) => {
          if (err) setErrors(err)
        })

        if (handled.success) {
          const trackBudgetAction = () => {
            const currentBudgetType = getCurrentBudgetType()
            const targetBudgetType = data.budgetType

            // Handle deletion case
            if (targetBudgetType === budgetTypesEnum.withoutBudget) {
              if (currentBudgetType !== budgetTypesEnum.withoutBudget) {
                track(trackingEvents.projectManagement.settings.deleteBudget)
              }
              return
            }

            // Get tracking budget type name
            const getTrackingBudgetType = (budgetType: string) => {
              switch (budgetType) {
                case budgetTypesEnum.projectFixedCost:
                  return project.isBillable ? 'Planned_cost_income' : 'Planned_cost'
                case budgetTypesEnum.totalBudget:
                  return 'total budget'
                default:
                  return budgetType
              }
            }

            const trackingBudgetType = getTrackingBudgetType(targetBudgetType)
            const isUpdate = currentBudgetType === targetBudgetType
            const isCreate = currentBudgetType === budgetTypesEnum.withoutBudget

            // Determine tracking parameters
            const trackingParams: Record<string, string> = {
              budgetType: trackingBudgetType,
            }

            // Add specific parameters for fixed cost planning
            if (targetBudgetType === budgetTypesEnum.projectFixedCost) {
              trackingParams.income = data.feeAmountCents ? 'yes' : 'no'

              // Add amountChanged for updates
              if (
                isUpdate &&
                project.budgetStrategy &&
                project.budgetStrategy.plannedCents &&
                project.budgetStrategy.budgetType === budgetTypesEnum.projectFixedCost
              ) {
                trackingParams.amountChanged =
                  data.plannedCents !== project.budgetStrategy.plannedCents / 100 ? 'yes' : 'no'
              } else if (!isCreate) {
                trackingParams.amountChanged = 'yes'
              }
            } else if (isUpdate && !isCreate) {
              // For other budget types, always mark amount as changed when updating
              trackingParams.amountChanged = 'yes'
            }

            // Track the appropriate event
            if (isCreate) {
              track(trackingEvents.projectManagement.settings.addBudget, trackingParams)
            } else {
              track(trackingEvents.projectManagement.settings.updateBudget, trackingParams)
            }
          }

          trackBudgetAction()

          app.setFlash({
            type: 'correct',
            message: i18n.t('common.saved_successfully'),
          })
          resetForm({ values: data })
        }

        return handled
      },
    })
  const handleFieldChanged = (
    fieldName: string,
    fieldValue: number | string | boolean | undefined,
    shouldValidate = false
  ) => {
    const numericFields = [
      'plannedCents',
      'plannedMinutes',
      'feeAmountCents',
      'fixedCostCents',
      'budgetAllocation',
    ]
    let normalizedValue = fieldValue
    if (numericFields.includes(fieldName) && typeof fieldValue === 'string') {
      normalizedValue = fieldValue === '' ? undefined : Number(fieldValue)
    }

    const newValues: IProjectBudgetValues = { ...values, [fieldName]: normalizedValue }

    if (fieldName === 'budgetType') {
      const serverBudgetType = getCurrentBudgetType()

      if (serverBudgetType === fieldValue) {
        const baseValues = getProjectBudgetDefaultValues()
        newValues.budgetAllocation = baseValues.budgetAllocation
        newValues.feeAmountCents = baseValues.feeAmountCents
        newValues.plannedCents = baseValues.plannedCents
        newValues.plannedMinutes = baseValues.plannedMinutes
        newValues.fixedCostCents = baseValues.fixedCostCents
      } else {
        newValues.budgetAllocation = undefined
        newValues.feeAmountCents = undefined
        newValues.plannedCents = undefined
        newValues.plannedMinutes = undefined
        newValues.fixedCostCents = undefined
        setErrors({})
      }
    }

    setValues(newValues, shouldValidate)
  }

  return (
    <>
      {featureAvailable ? (
        <Section
          illustration="money"
          title={i18n.t('project_management.settings.sections.budget_strategies_section.title')}
          description={i18n.t(
            'project_management.settings.sections.budget_strategies_section.description'
          )}
          paddingX="s0"
        >
          <Box gap="s24">
            <Box
              style={{ zIndex: values.budgetType === budgetTypesEnum.totalBudget ? '9999' : '1' }}
            >
              <CollapsableCard
                open={values.budgetType === budgetTypesEnum.totalBudget}
                Card={
                  <Box gap="s8" flexDirection="row" justifyContent="spaceBetween">
                    <Box flexDirection="column" gap="s4">
                      <Text.Normal size="300" weight="bold">
                        {i18n.t(
                          'project_management.settings.sections.budget_strategies_section.time_and_material.title'
                        )}
                      </Text.Normal>
                      <Text.Normal size="200">
                        {i18n.t(
                          'project_management.settings.sections.budget_strategies_section.time_and_material.subtitle'
                        )}
                      </Text.Normal>
                    </Box>
                    <RadioButton
                      value="timeAndMaterial"
                      checked={values.budgetType === budgetTypesEnum.totalBudget}
                      onChange={() => handleFieldChanged('budgetType', budgetTypesEnum.totalBudget)}
                    />
                  </Box>
                }
                Content={
                  values.budgetType === budgetTypesEnum.totalBudget ? (
                    <ProjectBudgetStrategiesForm
                      projectId={project.id}
                      onChangeValue={handleFieldChanged}
                      strategyType={budgetTypesEnum.totalBudget}
                      values={values}
                      errors={errors}
                      isOwner={!!project._policies?.assignOwner.can}
                      hasCostsFeature={hasCostsFeature}
                      hasBudgetFeature
                      fixedCostCents={values.fixedCostCents}
                      shouldShowSaveButton={dirty}
                      onSubmit={handleSubmit}
                    />
                  ) : null
                }
              />
            </Box>
            <Box
              style={{
                zIndex: values.budgetType === budgetTypesEnum.projectFixedCost ? '9999' : '1',
              }}
            >
              <CollapsableCard
                open={values.budgetType === budgetTypesEnum.projectFixedCost}
                Card={
                  <Box gap="s8" flexDirection="row" justifyContent="spaceBetween">
                    <Box flexDirection="column" gap="s4">
                      <Text.Normal size="300" weight="bold">
                        {i18n.t(
                          'project_management.settings.sections.budget_strategies_section.fixed_price.title'
                        )}
                      </Text.Normal>
                      <Text.Normal size="200">
                        {i18n.t(
                          'project_management.settings.sections.budget_strategies_section.fixed_price.subtitle'
                        )}
                      </Text.Normal>
                    </Box>
                    <RadioButton
                      value="fixedPrice"
                      checked={values.budgetType === budgetTypesEnum.projectFixedCost}
                      onChange={() =>
                        handleFieldChanged('budgetType', budgetTypesEnum.projectFixedCost)
                      }
                    />
                  </Box>
                }
                Content={
                  values.budgetType === budgetTypesEnum.projectFixedCost ? (
                    <ProjectBudgetStrategiesForm
                      projectId={project.id}
                      onChangeValue={handleFieldChanged}
                      strategyType={budgetTypesEnum.projectFixedCost}
                      values={values}
                      errors={errors}
                      isOwner={!!project._policies?.assignOwner.can}
                      hasCostsFeature={hasCostsFeature}
                      hasBudgetFeature
                      fixedCostCents={values.fixedCostCents}
                      shouldShowSaveButton={dirty}
                      onSubmit={handleSubmit}
                    />
                  ) : null
                }
              />
            </Box>
            <Box
              style={{ zIndex: values.budgetType === budgetTypesEnum.withoutBudget ? '9999' : '1' }}
            >
              <CollapsableCard
                open={values.budgetType === budgetTypesEnum.withoutBudget}
                Card={
                  <Box gap="s8" flexDirection="row" justifyContent="spaceBetween">
                    <Box flexDirection="column" gap="s4">
                      <Text.Normal size="300" weight="bold">
                        {i18n.t(
                          'project_management.settings.sections.budget_strategies_section.no_budget.title'
                        )}
                      </Text.Normal>
                      <Text.Normal size="200">
                        {i18n.t(
                          'project_management.settings.sections.budget_strategies_section.no_budget.subtitle'
                        )}
                      </Text.Normal>
                    </Box>
                    <RadioButton
                      value="noBudget"
                      checked={values.budgetType === budgetTypesEnum.withoutBudget}
                      onChange={() =>
                        handleFieldChanged('budgetType', budgetTypesEnum.withoutBudget)
                      }
                    />
                  </Box>
                }
                Content={
                  values.budgetType === budgetTypesEnum.withoutBudget ? (
                    <ProjectBudgetStrategiesForm
                      projectId={project.id}
                      onChangeValue={handleFieldChanged}
                      strategyType={budgetTypesEnum.withoutBudget}
                      values={values}
                      errors={errors}
                      isOwner={!!project._policies?.assignOwner.can}
                      hasCostsFeature={hasCostsFeature}
                      hasBudgetFeature
                      fixedCostCents={values.fixedCostCents}
                      shouldShowSaveButton={dirty}
                      onSubmit={handleSubmit}
                    />
                  ) : null
                }
              />
            </Box>
          </Box>
        </Section>
      ) : (
        <Section.Form
          illustration="money"
          title={i18n.t('project_management.planning.title')}
          description={i18n.t('project_management.planning.description')}
          onSubmit={handleSubmit}
          submitLabel={i18n.t('common.submit')}
          isDirty={dirty}
          paddingX="s0"
        >
          <ProjectBudgetForm
            onChangeValue={handleFieldChanged}
            values={values}
            errors={errors}
            isBillable={project.isBillable ?? false}
            isOwner={!!project._policies?.assignOwner.can}
            hasCostsFeature={hasCostsFeature}
            hasBudgetFeature
          />
        </Section.Form>
      )}
    </>
  )
}
